import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

/**
 * CourseBadge Component
 * 
 * Displays a badge indicating the course type (synchronous or asynchronous).
 * Follows the Instituto Valparaíso design system.
 * 
 * - "En Vivo" badge for synchronous courses (gradient amber-orange)
 * - "Autoguiado" badge for asynchronous courses (gradient emerald-teal)
 * 
 * @param {string} type - Course type: 'synchronous' or 'asynchronous'
 * @param {string} size - Size variant: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} className - Additional CSS classes
 */
export default function CourseBadge({ 
  type = 'asynchronous', 
  size = 'md',
  className = '' 
}) {
  // Badge configuration based on course type
  const badgeConfig = {
    synchronous: {
      label: 'En Vivo',
      gradient: 'from-amber-500 to-orange-500',
      bgGlow: 'shadow-amber-500/30',
    },
    asynchronous: {
      label: 'Autoguiado',
      gradient: 'from-emerald-500 to-teal-500',
      bgGlow: 'shadow-emerald-500/30',
    },
  };

  // Size variants
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
  };

  // Get configuration for the current type, default to asynchronous if invalid
  const config = badgeConfig[type] || badgeConfig.asynchronous;

  return (
    <motion.span
      className={`
        inline-flex items-center gap-1.5
        ${sizeClasses[size]}
        rounded-full
        bg-gradient-to-r ${config.gradient}
        text-white font-medium
        shadow-lg ${config.bgGlow}
        ${className}
      `}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.05 }}
    >
      {/* Live indicator dot for synchronous courses */}
      {type === 'synchronous' && (
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
        </span>
      )}
      {config.label}
    </motion.span>
  );
}

CourseBadge.propTypes = {
  type: PropTypes.oneOf(['synchronous', 'asynchronous']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};
